/**
 * The points of a station map by category: colours, icon, and whether the legend lists them — the old
 * kiosk's (frontend/.../station-map/config.ts, PoiMarker.tsx), with lucide icons for its hand-drawn ones.
 * Category names are words: `trains.poi.<category>` in strings.ts.
 */
import {
  Accessibility, Armchair, Banknote, Bus, CarTaxiFront, Cross, DoorOpen, Info, Luggage, MapPin, Sofa, Ticket,
  Toilet, TrainTrack, TramFront, Utensils, Wifi, type LucideIcon,
} from 'lucide-react';

export interface PoiStyle {
  icon: LucideIcon;
  /** fill of the marker, its ring, and the icon */
  bg: string;
  border: string;
  ink: string;
}

export const POI_STYLES: Readonly<Record<string, PoiStyle>> = {
  bathroom: { icon: Toilet, bg: '#DBEAFE', border: '#3B82F6', ink: '#1D4ED8' },
  ticket_office: { icon: Ticket, bg: '#FEF3C7', border: '#F59E0B', ink: '#B45309' },
  waiting_room: { icon: Armchair, bg: '#F3E8FF', border: '#A855F7', ink: '#7C3AED' },
  left_luggage: { icon: Luggage, bg: '#FFEDD5', border: '#F97316', ink: '#C2410C' },
  metro: { icon: TramFront, bg: '#FEE2E2', border: '#EF4444', ink: '#B91C1C' },
  bus: { icon: Bus, bg: '#DCFCE7', border: '#22C55E', ink: '#15803D' },
  taxi: { icon: CarTaxiFront, bg: '#FEF9C3', border: '#EAB308', ink: '#A16207' },
  atm: { icon: Banknote, bg: '#D1FAE5', border: '#10B981', ink: '#047857' },
  pharmacy: { icon: Cross, bg: '#FCE7F3', border: '#EC4899', ink: '#BE185D' },
  food: { icon: Utensils, bg: '#FED7AA', border: '#FB923C', ink: '#C2410C' },
  wifi: { icon: Wifi, bg: '#E0E7FF', border: '#6366F1', ink: '#4338CA' },
  platform: { icon: TrainTrack, bg: '#F1F5F9', border: '#64748B', ink: '#334155' },
  exit: { icon: DoorOpen, bg: '#DCFCE7', border: '#22C55E', ink: '#15803D' },
  info: { icon: Info, bg: '#DBEAFE', border: '#3B82F6', ink: '#1D4ED8' },
  sala_blu: { icon: Accessibility, bg: '#DBEAFE', border: '#1E3A8A', ink: '#1E3A8A' },
  lounge: { icon: Sofa, bg: '#FCE7EA', border: '#9F1239', ink: '#881337' },
  you_are_here: { icon: MapPin, bg: '#DC2626', border: '#B91C1C', ink: '#FFFFFF' },
};

/** A category the map does not know yet still gets a marker. */
export const UNKNOWN_POI: PoiStyle = { icon: MapPin, bg: '#F1F5F9', border: '#64748B', ink: '#334155' };

export const YOU_ARE_HERE = 'you_are_here';

/** On the map, not in the legend (as on the old kiosk). */
export const NOT_IN_LEGEND: ReadonlySet<string> = new Set(['food', 'info', YOU_ARE_HERE]);

export const styleOf = (category: string): PoiStyle => POI_STYLES[category] ?? UNKNOWN_POI;
